// The wait line under a Focus Refine screen — see `./wait-status` for why every
// word of it is a server fact.
//
// It re-renders on its own every `WAIT_TICK_MS` so the minutes move; nothing
// here is a deadline. `useHeldFor` is the one timer shape the page keeps: it
// says a server fact has held still for a while (idle with nothing open; no
// workspace at all), never that something has been quiet.

import { useEffect, useState, type ReactNode } from "react";

import { useT, type MessageKey } from "../i18n/index.js";

import {
  WAIT_TICK_MS,
  waitStatusOf,
  type WaitActivity,
  type WaitInput,
  type WorkInfo,
} from "./wait-status";

/**
 * True once `condition` has held continuously for `ms`. Any flip back resets
 * it. Waits on the fact, re-armed by nothing else.
 */
export const useHeldFor = (condition: boolean, ms: number): boolean => {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (!condition) {
      setHeld(false);
      return;
    }
    const timer = setTimeout(() => setHeld(true), ms);
    return () => clearTimeout(timer);
  }, [condition, ms]);
  return condition && held;
};

const ACTIVITY_KEY: Record<WaitActivity, MessageKey> = {
  code: "focus.wait.activity.code",
  issue: "focus.wait.activity.issue",
  card: "focus.wait.activity.card",
  size: "focus.wait.activity.size",
  design: "focus.wait.activity.design",
  milestones: "focus.wait.activity.milestones",
};

const minutesOf = (ms: number | null): number =>
  ms === null ? 0 : Math.floor(ms / 60_000);

function WorkingLines({ work }: { readonly work: WorkInfo }) {
  const t = useT();
  const minutes = minutesOf(work.elapsedMs);
  return (
    <>
      <div className="focus-note">
        {minutes >= 1
          ? t("focus.wait.workingFor", { minutes })
          : t("focus.wait.working")}
      </div>
      {work.activity !== null && (
        <div className="focus-note">{t(ACTIVITY_KEY[work.activity])}</div>
      )}
      {work.long && <div className="focus-note">{t("focus.wait.long")}</div>}
    </>
  );
}

export function WaitLine({ input }: { readonly input: WaitInput }) {
  const t = useT();
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), WAIT_TICK_MS);
    return () => clearInterval(id);
  }, []);
  const status = waitStatusOf(input, Date.now());
  let lines: ReactNode = null;
  switch (status.kind) {
    // The page's own socket is said once, on EVERY screen, by
    // `ConnectionLine` — not here, where only the pre-card and thinking steps
    // would ever show it. The server facts below are stale while it is down.
    case "offline":
    case "unreachable":
      lines = null;
      break;
    case "reconnecting":
      lines = <div className="focus-note">{t("focus.wait.reconnecting")}</div>;
      break;
    case "starting": {
      const minutes = minutesOf(status.elapsedMs);
      // Nothing to add to the phase headline yet: draw no empty box.
      if (minutes < 1 && !status.runnerLong && status.working === null) break;
      lines = (
        <>
          {minutes >= 1 && (
            <div className="focus-note">{t("focus.wait.soFar", { minutes })}</div>
          )}
          {status.runnerLong && (
            <div className="focus-note">{t("focus.wait.runnerLong")}</div>
          )}
          {status.working !== null && <WorkingLines work={status.working} />}
        </>
      );
      break;
    }
    case "working":
      lines = <WorkingLines work={status} />;
      break;
    default:
      lines = null;
  }
  if (lines === null) return null;
  return (
    <div className="focus-wait" role="status" aria-live="polite">
      {lines}
    </div>
  );
}

/**
 * The page's own socket is down (`offline`: one this mount had; `unreachable`:
 * a first connect that has not opened). Drawn above every Focus Refine screen,
 * so the question, grind and tail screens say it too. Offers nothing: the
 * socket retries on its own.
 */
export function ConnectionLine({ kind }: { readonly kind: "offline" | "unreachable" }) {
  const t = useT();
  return (
    <div className="focus-wait focus-connection" role="status" aria-live="polite">
      <div className="focus-note">
        {t(kind === "offline" ? "focus.wait.offline" : "focus.wait.unreachable")}
      </div>
    </div>
  );
}
